import {
  rememberPostLoginRedirect,
  safeRedirectPath,
} from '@/lib/safeRedirectPath'
import { useAuthStore } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import HomeView from '@/views/HomeView.vue'
import LoginView from '@/views/LoginView.vue'
import LogoutView from '@/views/LogoutView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import Bugsnag from '@bugsnag/js'
import mixpanel from 'mixpanel-browser'
import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
} from 'vue-router'

declare module 'vue-router' {
  interface RouteMeta {
    windowTitle?: string
  }
}

const SHOP_LOGIN_URL = 'https://bowercollective.com/account/login'

const redirectToShopLogin = (path: string) => {
  const redirectTo = safeRedirectPath(path) ?? '/'
  rememberPostLoginRedirect(redirectTo)
  const returnPath = `/account?redirect_to=${encodeURIComponent(redirectTo)}`
  window.location.assign(
    `${SHOP_LOGIN_URL}?return_url=${encodeURIComponent(returnPath)}`,
  )
}

const authenticatedRouteGuard = async (to: RouteLocationNormalized) => {
  const authStore = useAuthStore()
  try {
    await authStore.authenticate()
    if (!authStore.isAuthenticated) {
      redirectToShopLogin(to.fullPath)
      return false
    }
  } catch (e) {
    if (e && (e as Error).name === 'FetchError') {
      if ((e as { res: Response }).res.status === 401) {
        redirectToShopLogin(to.fullPath)
        return false
      } else {
        return '/login'
      }
    } else {
      Bugsnag.notify(e as Error)
      return '/login'
    }
  }
}

const getAuthedUser = () => useAuthStore().user

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      props: () => ({
        authedUser: getAuthedUser(),
      }),
      beforeEnter: [authenticatedRouteGuard],
    },
    {
      path: '/shop',
      name: 'shop',
      component: () => import('@/views/ShopView.vue'),
      props: (route) => ({
        authedUser: getAuthedUser(),
        search: route.query.q,
        context: route.query.context ?? (null as 'next-order' | 'cart' | null),
      }),
      beforeEnter: [authenticatedRouteGuard],
    },
    {
      path: '/shop/:collectionId',
      name: 'shop-collection',
      component: () => import('@/views/ShopCollectionView.vue'),
      beforeEnter: [authenticatedRouteGuard],
      props: (route) => ({
        authedUser: getAuthedUser(),
        handle: route.params.collectionId,
      }),
      meta: {
        windowTitle: 'Browse category',
      },
    },
    {
      path: '/next-order',
      name: 'next-order',
      component: () => import('@/views/NextOrderView.vue'),
      props: () => ({
        authedUser: getAuthedUser(),
      }),
      beforeEnter: [authenticatedRouteGuard],
      meta: {
        windowTitle: 'Next Order',
      },
    },
    {
      path: '/out-of-stock',
      name: 'out-of-stock',
      component: () => import('@/views/OutOfStockView.vue'),
      props: () => ({
        authedUser: getAuthedUser(),
      }),
      beforeEnter: [authenticatedRouteGuard],
      meta: {
        windowTitle: 'Out of stock',
      },
    },
    {
      path: '/product-subscriptions',
      name: 'product-subscriptions',
      component: () => import('@/views/ProductSubscriptionsView.vue'),
      props: () => ({
        authedUser: getAuthedUser(),
      }),
      beforeEnter: [authenticatedRouteGuard],
      meta: {
        windowTitle: 'Product subscriptions',
      },
    },
    {
      path: '/upcoming-orders',
      name: 'upcoming-orders',
      component: () => import('@/views/UpcomingOrdersView.vue'),
      props: () => ({
        authedUser: getAuthedUser(),
      }),
      beforeEnter: [authenticatedRouteGuard],
    },
    {
      path: '/upcoming-orders/:scheduledAt',
      name: 'upcoming-order',
      component: () => import('@/views/UpcomingOrderView.vue'),
      props: (route) => ({
        authedUser: getAuthedUser(),
        scheduledAt: route.params.scheduledAt,
      }),
      beforeEnter: [authenticatedRouteGuard],
    },
    {
      path: '/product/:productId',
      name: 'product',
      component: () => import('@/views/ProductView.vue'),
      props: (route) => ({
        authedUser: getAuthedUser(),
        productId: route.params.productId,
        target: route.query.target,
      }),
      beforeEnter: [authenticatedRouteGuard],
      meta: {
        windowTitle: 'Product details',
      },
    },
    {
      path: '/cart',
      name: 'cart',
      component: () => import('@/views/CartView.vue'),
      props: () => ({
        authedUser: getAuthedUser(),
      }),
      beforeEnter: [
        authenticatedRouteGuard,
        () => {
          const cartStore = useCartStore()
          if (cartStore.items.length === 0) {
            return '/shop?context=cart'
          }
        },
      ],
      meta: {
        windowTitle: 'Cart',
      },
    },
    {
      path: '/previous-orders',
      name: 'previous-orders',
      component: () => import('@/views/PreviousOrdersView.vue'),
      props: () => ({
        authedUser: getAuthedUser(),
      }),
      beforeEnter: [authenticatedRouteGuard],
      meta: {
        windowTitle: 'Previous orders',
      },
    },
    {
      path: '/orders/:orderId',
      name: 'order',
      component: () => import('@/views/OrderView.vue'),
      props: (route) => ({
        authedUser: getAuthedUser(),
        orderId: route.params.orderId,
      }),
      beforeEnter: [authenticatedRouteGuard],
      meta: {
        windowTitle: 'Order details',
      },
    },
    {
      path: '/addresses',
      name: 'addresses',
      component: () => import('@/views/AddressesView.vue'),
      props: () => ({
        authedUser: getAuthedUser(),
      }),
      beforeEnter: [authenticatedRouteGuard],
    },
    {
      path: '/addresses/:id',
      name: 'address-edit',
      component: () => import('@/views/AddressEditView.vue'),
      props: (route) => ({
        authedUser: getAuthedUser(),
        addressId: route.params.id,
      }),
      beforeEnter: [authenticatedRouteGuard],
    },
    {
      path: '/payment-methods',
      name: 'payment-methods',
      component: () => import('@/views/PaymentMethodsView.vue'),
      props: () => ({
        authedUser: getAuthedUser(),
      }),
      beforeEnter: [authenticatedRouteGuard],
    },
    {
      path: '/account-details',
      name: 'account-details',
      component: () => import('@/views/AccountDetailsView.vue'),
      props: () => ({
        authedUser: getAuthedUser(),
      }),
      beforeEnter: [authenticatedRouteGuard],
    },
    {
      path: '/account-details/edit',
      name: 'account-details-edit',
      component: () => import('@/views/AccountEditView.vue'),
      props: () => ({
        authedUser: getAuthedUser(),
      }),
      beforeEnter: [authenticatedRouteGuard],
    },
    {
      path: '/login',
      name: 'Login',
      component: LoginView,
    },
    {
      path: '/logout',
      name: 'Logout',
      component: LogoutView,
    },
    {
      path: '/style-guide',
      name: 'Style Guide',
      component: () => import('@/views/StyleGuideView.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: NotFoundView,
      props: () => ({
        authedUser: getAuthedUser(),
      }),
    },
    {
      path: '/packaging-returns',
      name: 'packaging-returns',
      component: () => import('@/views/PackagingReturnsView.vue'),
      props: () => ({
        authedUser: getAuthedUser(),
      }),
      meta: {
        windowTitle: 'Packaging returns',
      },
      beforeEnter: [
        async () => {
          const authStore = useAuthStore()
          await authStore.authenticate()
        },
      ],
    },
    {
      path: '/quick-actions/:action_name',
      name: 'quick-actions',
      meta: {
        windowTitle: 'Quick actions',
      },
      props: (route) => ({
        actionName: route.params.action_name,
      }),
      component: () => import('@/views/QuickActionsView.vue'),
    },
  ],
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0, behavior: 'smooth' }
    }
  },
})

router.afterEach((to) => {
  mixpanel.track('page_view', { url: to.fullPath })
  if (to.meta.windowTitle) {
    window.document.title = `${to.meta.windowTitle} – Bower Collective`
  } else {
    window.document.title = `Your account – Bower Collective`
  }
})

// Add global click handler for a tags
document.addEventListener('click', (e) => {
  const target = e.target as HTMLElement
  const anchor = target.closest('a')

  if (anchor && anchor.href) {
    const url = new URL(anchor.href)
    // Only handle internal links (same origin)
    if (url.origin === window.location.origin) {
      e.preventDefault()
      router.push(url.pathname + url.search + url.hash)
    }
  }
})

export default router
